/**
 * The cell addresses a held row's verbs share (`SheetRow.Handle`): which
 * address a write, clear, move or replace takes (`existingAddress`,
 * `durableAddress`, `timepointOf`, `onRow`, `heldTimepoint`), the held cells
 * a read hands out (`heldDateCellOf`, `heldTimepointCellOf`), the window a
 * range read covers and its fetch (`CellWindow`), a move's take-back on the
 * row's cell cache, and the refusals each verb reports. Pure functions over
 * a row's id, cache and owners: no state, and nothing of the client.
 */
import type { ListenerErrorSink } from "@tento-core/listener-errors";
import type { Option } from "@tento-lona/sheets";
import { NaiveDate, DateTime, SemanticTime, Utc } from "@tento-chrono";
import { TypedApi } from "@tento-core/typed-api";
import { RowLocalId } from "@tento-lona/sheets";
import { Network } from "../network.ts";
import { MoveWrites } from "@tento-lona/sheets/internal-sdk";
import { CellHandle } from "../cells/cell-handle.ts";
import { RowRangeCellHandle } from "../cells/range-cell-handle.ts";
import { CellEntry } from "@tento-lona/sheets";
import { CellTarget } from "../cells/cell-target.ts";
import { CellWriteError, SdkRequestError } from "../errors.ts";
import { RowCellCache } from "../sync/row-cell-cache.ts";
import { RowNetworkKey } from "@tento-lona/sheets";
import type { SheetRow } from "./sheet-row.ts";

/** Why a `range` target has nothing to clear. */
const RANGE_CLEAR =
  "a range addresses no existing cell — clear a held cell (CellTarget.cell)";

/** Why an `add` / `addWithId` target has nothing to clear. */
const ADD_CLEAR =
  "an add names a new cell, not one the row holds — clear a held cell (CellTarget.cell / CellTarget.keyed)";

/**
 * The address a `clear` clears. Its type already excludes a `range`, an
 * `add` and an `addWithId`; one from an untyped caller rejects here,
 * nothing sent.
 */
export function existingAddress(target: CellTarget, verb: string): CellTarget.OnDate | CellTarget.OnCell {
  if (target.kind === "range") throw unaddressableCell("clear-timepoint-cell", verb, RANGE_CLEAR);
  if (target.kind === "add" || target.kind === "add-with-id") throw unaddressableCell("clear-cell", verb, ADD_CLEAR);
  return target;
}

/**
 * Take a refused move back on the row's handle: the cell goes back to
 * `back.to` when its id is still a timepoint cell of the row; when it is
 * gone (a replace or a delete the server accepted took it), nothing is
 * resurrected — the row is marked stale, so its next read re-reads it.
 */
export function takeBackMove(rh: RowCellCache, cell: CellEntry.Timepoint, back: MoveWrites.Shown<DateTime.Range<Utc>>): void {
  const at = NaiveDate.Partial.fromDate(back.from.start.date);
  if (rh.addressOfExisting(cell.id, at)?.store === "custom") {
    rh.optimisticMoveTimepointCell(back.from, back.to, cell.id, cell.value);
    return;
  }
  rh.markStale();
}

/**
 * `target`, checked to address `row`: a held cell another row read (its
 * `row` stamp) — and every target on a row whose cells are computed
 * (`Capabilities.canWriteCells` false: a formula row, a client-side
 * provider's row) — is refused with `CellWriteError` (outcome `unaddressable`),
 * nothing sent — also one addressed by id (`CellTarget.keyed`,
 * `CellTarget.timepoint`), which names the row that holds it. A new cell
 * (`CellTarget.add`, `addWithId`, `range`) names no row and passes.
 */
export function onRow(
  target: CellTarget,
  row: SheetRow,
  verb: "write" | "writeDurable" | "move" | "replace" | "clear" | "clearDurable",
): void {
  const timepoint = target.kind === "timepoint-cell" || target.kind === "range";
  const step: CellWriteError.Step = verb === "move" || verb === "replace"
    ? relocateStep(verb)
    : verb === "write" || verb === "writeDurable"
    ? (timepoint ? "write-timepoint-cell" : "write-cell")
    : (timepoint ? "clear-timepoint-cell" : "clear-cell");
  if (!row.capabilities().canWriteCells) {
    throw unaddressableCell(step, verb, READ_ONLY_CELLS);
  }
  if (target.kind !== "dated-cell" && target.kind !== "timepoint-cell") return;
  const from = target.cell.row;
  if (from === row.id) return;
  throw unaddressableCell(
    step,
    verb,
    `the cell belongs to row ${from.toString()}, not ${row.id.toString()} (it was read from that row, ` +
      "or addressed on it by id) — write it through that row",
  );
}

/** Why a row whose cells are computed refuses a cell write. */
const READ_ONLY_CELLS =
  "the row's cells are computed (a formula row, or a client-side provider's) and take no write — " +
  "check row.capabilities().canWriteCells";

/**
 * The refusal of a cell write whose target the verb cannot address: never
 * sent (`outcome: "unaddressable"`).
 */
function unaddressableCell(
  step: CellWriteError.Step,
  verb: string,
  why: string,
): CellWriteError {
  return new CellWriteError({
    step,
    method: "POST",
    path: "/api/rows/{rowKey}/cells",
    failure: { outcome: "unaddressable" },
    details: `${verb}: ${why}`,
  });
}

/** The step a durable verb's refused timepoint target reports. */
function timepointStep(verb: "writeDurable" | "clearDurable"): CellWriteError.Step {
  return verb === "writeDurable" ? "write-timepoint-cell" : "clear-timepoint-cell";
}

/**
 * The date-keyed address a durable cell write (`writeDurable`,
 * `clearDurable`) hands off. Its type already admits only a date-keyed
 * address; a timepoint one from an untyped caller (a new timepoint cell or
 * a held one, whose id the server mints on the write) throws here, before
 * anything is sent — and the verb reports it through `settled`
 * (`reportThroughSettled`).
 */
export function durableAddress(
  target: CellTarget,
  verb: "writeDurable" | "clearDurable",
): { readonly date: NaiveDate.Partial; readonly id: Option<string> } {
  switch (target.kind) {
    case "date":
      return { date: target.date, id: null };
    case "dated-cell":
      return { date: target.cell.date, id: target.cell.id };
    case "add-with-id":
      return { date: target.date, id: target.id };
    case "add":
      throw unaddressableCell(verb === "writeDurable" ? "write-cell" : "clear-cell", verb, ADD_NOT_DURABLE);
    case "range":
    case "timepoint-cell":
      throw unaddressableCell("write-timepoint-cell", verb, TIMEPOINT_NOT_DURABLE);
  }
}


/** Why an `add` has no durable form. */
const ADD_NOT_DURABLE =
  "an added cell's id is minted by the server on the write, and a " +
  "debounced hand-off never returns it — use the checked `write`";

/** Why a timepoint write has no durable form. */
const TIMEPOINT_NOT_DURABLE =
  "a timepoint cell's id is minted by the server on the write, and a " +
  "debounced hand-off never returns it — use the checked `write`";

/** The step a `move` / `replace` refusal reports: `move-cell` / `replace-cell`. */
export function relocateStep(verb: "move" | "replace"): CellWriteError.Step {
  return verb === "move" ? "move-cell" : "replace-cell";
}

/**
 * The held timepoint cell a `move` moves. Its type already admits only a
 * timepoint cell; any other address from an untyped caller throws here (a
 * date-keyed cell has no range).
 */
export function timepointOf(target: CellTarget, verb: "move" | "replace"): CellTarget.TimepointCell {
  if (target.kind === "timepoint-cell") return target.cell;
  throw unaddressableCell(
    relocateStep(verb),
    verb,
    "a date-keyed cell has no range to move — write it at its new date",
  );
}

/**
 * The timepoint cell `cell` names, as `rh` holds it now — its current range
 * (a move's optimistic range included) and value, read by its id — for a
 * verb that re-posts or replaces it (`write` in place, `move`). An id the
 * row does not hold, or a row with no cell handle, is refused with
 * `CellWriteError` (outcome `unaddressable`, `step`), nothing sent.
 */
export function heldTimepoint(
  rh: Option<RowCellCache>,
  cell: CellTarget.TimepointCell,
  step: CellWriteError.Step,
  verb: string,
): CellEntry.Timepoint {
  const held = rh?.peekTimepointCell(cell.id) ?? null;
  if (held != null) return held;
  throw unaddressableCell(
    step,
    verb,
    `the row holds no timepoint cell ${cell.id} — read it first (row.cells(range)) or write a new one (CellTarget.range)`,
  );
}

/**
 * The entries a sheet-wide read (`handle.cells`) hands out for `row`: the
 * model tier's cells stamped with their kind and the row, as `row.cells`
 * hands them out, so they feed `CellTarget.cell`.
 *
 * @internal SDK-private (`SheetHandle.cells`); `lib.ts` does not export it.
 */
export function heldDateCellOf(row: RowLocalId, entry: CellEntry): SheetRow.DateCell {
  return datedCell(row, entry);
}

/** See `heldDateCellOf`: a timepoint cell. @internal */
export function heldTimepointCellOf(row: RowLocalId, entry: CellEntry.Timepoint): SheetRow.TimepointCell {
  return timepointCell(row, entry);
}

/** A date-keyed cell of the model tier as `row`'s read hands it out. */
export function datedCell(row: RowLocalId, entry: CellEntry): SheetRow.DateCell {
  return {
    kind: "date",
    row,
    date: entry.date,
    id: entry.id ?? null,
    value: entry.value,
    style: entry.style,
  };
}

/** A timepoint cell of the model tier as `row`'s read hands it out. */
function timepointCell(row: RowLocalId, entry: CellEntry.Timepoint): SheetRow.TimepointCell {
  return {
    kind: "timepoint",
    row,
    id: entry.id,
    dtr: entry.dtr,
    value: entry.value,
    style: entry.style,
  };
}

/**
 * The window a row's one cell read (`row.cells(range)`) covers: the days of
 * its date-keyed cells, the UTC range of its timepoint cells.
 */
export type CellWindow = {
  readonly days: NaiveDate.Range;
  readonly utc: DateTime.Range<Utc>;
};

export namespace CellWindow {
  /** A date range reads its days and their UTC bounds; a UTC range reads
   *  itself and every day from its first to its last date. */
  export function of(range: NaiveDate.Range | DateTime.Range<Utc>): CellWindow {
    if (range instanceof NaiveDate.Range) {
      return {
        days: range,
        utc: new DateTime.Range(
          DateTime.fromDse(range.start.dse, Utc),
          DateTime.fromDse(range.end.dse, Utc),
        ),
      };
    }
    return {
      days: new NaiveDate.Range(range.start.date, range.end.date.addDays(1)),
      utc: range,
    };
  }

  /** Every cell `rh` holds in `span`, tagged by kind — no lane converted
   *  into the other — and stamped with `row`, the row handing them out. */
  export function cached(rh: RowCellCache, span: CellWindow, row: RowLocalId): SheetRow.Cell[] {
    const out: SheetRow.Cell[] = [];
    for (const entry of rh.getAllInRange(span.days)) out.push(datedCell(row, entry));
    for (const entry of rh.getAllTimepointsInRange(span.utc)) out.push(timepointCell(row, entry));
    return out;
  }

  /**
   * The progressive read of `span`: over the row's handle when it has one
   * (fetching `span.utc` through it), else one checked `GET` of the row's
   * cells, each wire cell decoded as the address it carries. `owner` is the
   * sheet handing the read out (`null`: none); `listenerErrors` takes a
   * subscriber's throw when the row has no cache (a cached read reports to
   * the cache's sink).
   */
  export function read(
    row: RowLocalId,
    rowKey: string,
    span: CellWindow,
    api: TypedApi<Network.Routes>,
    rh: Option<RowCellCache>,
    owner: Option<CellHandle.Owner>,
    listenerErrors: ListenerErrorSink,
  ): RowRangeCellHandle<SheetRow.Cell> {
    const fetchEntries = async (): Promise<SheetRow.Cell[]> => {
      // Checked: a refused or unanswered read rejects (the handle's
      // `onError`, the awaited promise) instead of reading as `[]`; a 404
      // (no such row) is `[]`.
      const path = "/api/rows/{rowKey}/cells";
      const cells = await SdkRequestError.checkRequest("cells", "get-cells", "GET", path, () =>
        SdkRequestError.notFoundAsNull(() =>
          api.getResult(path, {
            path: { rowKey: RowNetworkKey.forRead(RowNetworkKey.fromWire(rowKey)) },
            query: {
              start: span.days.start,
              end: span.days.end,
              mock_sync_delay: null,
              booking_id: null,
            },
          })
        ));
      const out: SheetRow.Cell[] = [];
      for (const cell of cells ?? []) {
        if (SemanticTime.partialDate(cell.date) == null) {
          const timepoint = CellEntry.Timepoint.tryFrom(cell);
          if (timepoint != null) out.push(timepointCell(row, timepoint));
          continue;
        }
        const dated = cell.id != null
          ? CellEntry.MultiCard.tryFrom(cell)
          : CellEntry.SingleCard.tryFrom(cell);
        if (dated != null) out.push(datedCell(row, dated));
      }
      return out;
    };
    return new RowRangeCellHandle<SheetRow.Cell>(
      fetchEntries,
      rh,
      rh == null ? null : () => cached(rh, span, row),
      span.utc,
      owner,
      listenerErrors,
    );
  }
}
